import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { FeatureFlagRolloutApi } from './FeatureFlagRolloutApi';
import { TIER_LIST } from './tierList';
import { Flag } from './entities/Flag';
import { FlagDetailsUpdate } from './entities/FlagDetailsUpdate';
import { FlagState, OverrideResult } from './entities/FlagState';
import { FeatureTicketSuggestion, JiraAssignee, JiraTicketKind, JiraTickets } from './entities/JiraTicket';
import { TierTarget } from './entities/TierTarget';
import { NewFlag } from './entities/NewFlag';
import { PrConfirmation } from './entities/PrConfirmation';
import { PrKind } from './entities/PrKind';
import { Stage } from './entities/Stage';
import { readTokens } from '../tonkeanTokens';

/** Any one of your Tonkean tokens, for the backend to read your email with. */
function anyTonkeanLogin(): { query: string; headers: Record<string, string> } {
  const [entry] = Object.entries(readTokens());
  if (!entry) {
    return { query: '', headers: {} };
  }
  const [env, token] = entry;
  return { query: `?env=${encodeURIComponent(env)}`, headers: { 'X-Tonkean-Token': token } };
}

export class FeatureFlagRolloutClient implements FeatureFlagRolloutApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  async getTierList() {
    return TIER_LIST;
  }

  listFlags(): Promise<Flag[]> {
    return this.request('/flags');
  }

  async getFlag(key: string): Promise<Flag | undefined> {
    const response = await this.fetch(`/flags/${encodeURIComponent(key)}`);
    if (response.status === 404) {
      return undefined;
    }
    return this.parse(response);
  }

  addFlag(flag: NewFlag): Promise<Flag> {
    return this.request('/flags', 'POST', flag);
  }

  updateFlagDetails(key: string, update: FlagDetailsUpdate): Promise<Flag> {
    return this.request(`/flags/${encodeURIComponent(key)}`, 'PATCH', update);
  }

  setStage(key: string, stage: Stage, options: { enteredAt?: string; reason?: string }): Promise<Flag> {
    return this.request(`/flags/${encodeURIComponent(key)}/stage`, 'PUT', { stage, ...options });
  }

  setTargetDone(key: string, targetId: string, done: boolean): Promise<Flag> {
    return this.request(`/flags/${encodeURIComponent(key)}/targets/${encodeURIComponent(targetId)}`, 'PUT', { done });
  }

  markTargetsDone(key: string, targetIds: string[]): Promise<Flag> {
    return this.request(`/flags/${encodeURIComponent(key)}/targets/done`, 'POST', { targetIds });
  }

  setPrUrl(key: string, kind: PrKind, url: string): Promise<Flag> {
    return this.request(`/flags/${encodeURIComponent(key)}/prs/${kind}`, 'PUT', { url });
  }

  setPrConfirmation(key: string, kind: PrKind, confirmation: PrConfirmation, confirmed: boolean, at?: string): Promise<Flag> {
    return this.request(`/flags/${encodeURIComponent(key)}/prs/${kind}/${confirmation}`, 'PUT', { confirmed, at });
  }

  getFlagState(env: string, flagKey: string, tonkeanToken: string): Promise<FlagState> {
    const path = `/environments/${encodeURIComponent(env)}/flags/${encodeURIComponent(flagKey)}/state`;
    return this.request(path, 'GET', undefined, { 'X-Tonkean-Token': tonkeanToken });
  }

  async listEnvironmentNames(): Promise<string[]> {
    const environments: { name: string }[] = await this.request('/environments');
    return environments.map(environment => environment.name);
  }

  setOffCheckPassed(key: string, passed: boolean, override?: { unchecked: string[]; reason: string }): Promise<Flag> {
    return this.request(`/flags/${encodeURIComponent(key)}/off-check`, 'PUT', { passed, override });
  }

  setAppPrNotNeeded(key: string, notNeeded: boolean): Promise<Flag> {
    return this.request(`/flags/${encodeURIComponent(key)}/app-pr-not-needed`, 'PUT', { notNeeded });
  }

  applyOverride(key: string, target: TierTarget, tonkeanToken: string): Promise<{ flag: Flag; result: OverrideResult }> {
    const path = `/flags/${encodeURIComponent(key)}/targets/${encodeURIComponent(target.id)}/override`;
    const body = { env: target.env, projectId: target.projectId, label: target.label };
    return this.request(path, 'POST', body, { 'X-Tonkean-Token': tonkeanToken });
  }

  setOverrideTrue(
    key: string,
    row: { env: string; projectId: string | null; label: string; targetId?: string },
    tonkeanToken: string,
  ): Promise<{ flag: Flag; result: OverrideResult }> {
    return this.request(`/flags/${encodeURIComponent(key)}/overrides`, 'POST', row, { 'X-Tonkean-Token': tonkeanToken });
  }

  openPreparedPr(key: string, kind: 'devCi' | 'defaultTrue'): Promise<{ flag: Flag; notice: string | null }> {
    return this.request(`/flags/${encodeURIComponent(key)}/prs/${kind}/open`, 'POST', {});
  }

  getGithubAccess(): Promise<{ configured: boolean; unreadable: string[] }> {
    return this.request('/credentials/github');
  }

  getJiraAccess(): Promise<{ configured: boolean; valid: boolean; name: string | null }> {
    return this.request('/credentials/jira');
  }

  getJiraTickets(key: string): Promise<JiraTickets> {
    return this.request(`/flags/${encodeURIComponent(key)}/jira`);
  }

  searchJiraUsers(query: string): Promise<{ name: string; email: string; avatarUrl: string | null }[]> {
    return this.request(`/jira/users?query=${encodeURIComponent(query)}`, 'GET');
  }

  suggestFeatureTickets(flagKey: string): Promise<FeatureTicketSuggestion[]> {
    return this.request(`/jira/feature-tickets?flagKey=${encodeURIComponent(flagKey)}`, 'GET');
  }

  getJiraAssignee(): Promise<JiraAssignee> {
    const { query, headers } = anyTonkeanLogin();
    return this.request(`/jira/assignee${query}`, 'GET', undefined, headers);
  }

  createJiraTicket(key: string, kind: JiraTicketKind): Promise<Flag> {
    const { query, headers } = anyTonkeanLogin();
    return this.request(`/flags/${encodeURIComponent(key)}/jira/${kind}${query}`, 'POST', {}, headers);
  }

  moveJiraTicket(key: string, kind: JiraTicketKind | 'feature', status: string): Promise<Flag> {
    return this.request(`/flags/${encodeURIComponent(key)}/jira/${kind}/move`, 'POST', { status });
  }

  getNoAccessEnvironments(): Promise<string[]> {
    return this.request('/me/no-access');
  }

  setNoAccess(env: string, noAccess: boolean): Promise<string[]> {
    return this.request(`/me/no-access/${encodeURIComponent(env)}`, 'PUT', { noAccess });
  }

  private async fetch(path: string, method = 'GET', body?: unknown, credentialHeaders: Record<string, string> = {}) {
    const baseUrl = await this.discoveryApi.getBaseUrl('feature-flag-rollout');
    const headers: Record<string, string> = { ...credentialHeaders };
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
    return this.fetchApi.fetch(`${baseUrl}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  private async parse<T>(response: Response): Promise<T> {
    const body = await response.json().catch(() => undefined);
    if (!response.ok) {
      throw new Error(body?.error ?? `${response.status} ${response.statusText}`);
    }
    return body as T;
  }

  private async request<T>(
    path: string,
    method = 'GET',
    body?: unknown,
    credentialHeaders?: Record<string, string>,
  ): Promise<T> {
    return this.parse(await this.fetch(path, method, body, credentialHeaders));
  }
}
